import React, { useEffect, useState } from 'react';
import { useQuery } from '@common/hooks';
import { RadioChangeEvent, FormInstance } from 'antd';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { Store } from 'antd/lib/form/interface';
import {
  DatePicker,
  ErrorHandler,
  Gutter,
  LoadingState,
  PhoneInput,
  Radio,
  Form,
  Col,
  Row,
  Input,
  Title,
} from '@common/components';
import { datetime, type DatetimeType } from '@common/services/datetime';
import { RoleSelect } from '@clinician-profile/components';
import {
  DateOfBirthQuery,
  SSNQuery,
  WorksProfileQuery,
  RolesQuery,
  RolesResponse,
} from '@clinician-profile/graphql';
import { SpecialtySelect } from '@institutions/components/specialty-select';
import { useCurrentEntity } from '@identity/hooks';

const { TextArea } = Input;

export const disabledDateOfBirth = (dateOfBirth: DatetimeType) =>
  dateOfBirth && dateOfBirth.valueOf() > datetime().add(-1, 'days').valueOf();

export const CandidateForm = ({
  worksProfileId,
  form,
  onFinish,
  formSubmitting,
}: {
  worksProfileId?: string;
  form: FormInstance;
  onFinish: (values: Store) => void;
  formSubmitting: boolean;
}) => {
  const [isInternational, setIsInternational] = useState(false);
  const [disabledPrimarySpecialties, setDisabledPrimarySpecialties] = useState<
    string[]
  >([]);
  const [disabledSecondarySpecialty, setDisabledSecondarySpecialty] =
    useState('');
  const currentEntity = useCurrentEntity();

  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery<RolesResponse>(RolesQuery);

  const {
    loading: loadingProfile,
    error: errorProfile,
    data: dataProfile,
  } = useQuery(WorksProfileQuery, {
    variables: { id: worksProfileId },
    skip: !worksProfileId,
  });

  const {
    loading: loadDob,
    error: errorWorksDob,
    data: dataDob,
  } = useQuery(DateOfBirthQuery, {
    variables: {
      id: worksProfileId,
    },
    skip: !worksProfileId,
  });

  const {
    loading: loadSsn,
    error: errorSsn,
    data: dataSsn,
  } = useQuery(SSNQuery, {
    variables: {
      id: worksProfileId,
    },
    skip: !worksProfileId,
  });

  useEffect(() => {
    if (dataProfile) {
      const type = dataProfile.worksProfile.profileType;
      setIsInternational(type === 'international');
      form.setFieldsValue({ profileType: type });
      setDisabledPrimarySpecialties(
        dataProfile.worksProfile?.secondarySpecialties?.map(
          (specialty: { id: string }) => specialty?.id
        )
      );
      setDisabledSecondarySpecialty(
        dataProfile.worksProfile?.primarySpecialty?.id
      );
    }
  }, [dataProfile, form]);

  if (rolesLoading || loadSsn || loadDob || loadingProfile) {
    return <LoadingState />;
  }

  const anyError = rolesError || errorSsn || errorWorksDob || errorProfile;
  if (anyError) {
    return (
      <ErrorHandler
        error={anyError}
        log="CandidateForm::NO_WORKS_USER_CACHED"
      />
    );
  }

  const dateOfBirth = dataDob?.worksProfileDateOfBirth?.dateOfBirth;
  const ssn = dataSsn?.worksProfileSsn;
  const worksProfile = dataProfile?.worksProfile;

  const onFinishFailed = ({ errorFields }: ValidateErrorEntity) => {
    form.scrollToField(errorFields[0].name);
  };

  const internationalAsDefault = currentEntity.internationalAsDefault || false;
  const internationalDefaultType = internationalAsDefault
    ? 'international'
    : 'domestic';
  const initialProfileType =
    worksProfile?.profileType || internationalDefaultType;

  const initialValues = {
    profileType: initialProfileType,
    firstName: worksProfile?.firstName,
    lastName: worksProfile?.lastName,
    phone: worksProfile?.phone,
    email: worksProfile?.email,
    profileSummary: worksProfile?.profileSummary,
    ssn: ssn,
    dateOfBirth: dateOfBirth && datetime(dateOfBirth),
    roleId: worksProfile?.role?.id,
    specialtyId: worksProfile?.primarySpecialty?.id,
    secondarySpecialtyIds: worksProfile?.secondarySpecialties?.map(
      (specialty: { id: string }) => specialty?.id
    ),
  };

  const checkProfileType = (event: RadioChangeEvent) => {
    form.resetFields(['phone']);

    const type = event.target.value;
    setIsInternational(type === 'international');
    form.setFieldsValue({ profileType: type });
  };

  return (
    <Form
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      form={form}
      initialValues={initialValues}
    >
      <Gutter direction="bottom" size="md">
        <Title size="h4" weight="bold">
          Personal Data
        </Title>
      </Gutter>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Form.Item
            label="First name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter a first name' }]}
          >
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Last name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter a last name' }]}
          >
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Form.Item
            label="Phone Number"
            name="phone"
            rules={[
              {
                required: !isInternational,
                message: "Please enter the candidate's phone number",
              },
              {
                required: !isInternational,
                pattern: !isInternational ? /^(\d{3}-){2}\d{4}$/ : undefined,
                message: 'Phone numbers must be in xxx-xxx-xxxx format',
              },
            ]}
          >
            {isInternational ? (
              <Input size="large" disabled={formSubmitting} maxLength={20} />
            ) : (
              <PhoneInput size="large" disabled={formSubmitting} />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: true,
                message: "Please enter the candidate's email",
              },
              {
                type: 'email',
                message: 'Please enter a valid email address',
              },
            ]}
          >
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Form.Item
            label="Date Of Birth"
            name="dateOfBirth"
            rules={[
              { required: true, message: 'Please enter a date of birth' },
            ]}
          >
            <DatePicker
              placeholder="mm/dd/yyyy"
              format="MM/DD/YYYY"
              size="large"
              disabledDate={disabledDateOfBirth}
              disabled={formSubmitting}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Social Security Number"
            name="ssn"
            rules={[
              {
                required: false,
                pattern: /\d{3}-\d{2}-\d{4}$/,
                message: 'SSN must be in xxx-xx-xxxx format',
              },
            ]}
          >
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>
      </Row>

      <Gutter direction="bottom" size="md">
        <Title size="h4" weight="bold">
          Working Profile
        </Title>
      </Gutter>

      <Form.Item label="Candidate Type" name="profileType">
        <Radio.Group
          defaultValue="domestic"
          buttonStyle="solid"
          onChange={checkProfileType}
          size="large"
        >
          <Radio.Button value="domestic">Domestic</Radio.Button>
          <Radio.Button value="international">International</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <RoleSelect
            roles={rolesData?.roles}
            disabled={formSubmitting}
            labelKey="name"
          />
        </Col>
        <Col span={12}>
          <SpecialtySelect
            disabled={formSubmitting}
            label="Primary Specialty"
            disabledOptions={disabledPrimarySpecialties}
            onSelect={(value) => setDisabledSecondarySpecialty(value)}
          />
        </Col>
      </Row>

      <SpecialtySelect
        disabled={formSubmitting}
        label="Secondary Specialties"
        formItemName="secondarySpecialtyIds"
        placeholder="Select specialties"
        required={false}
        multiple
        disabledOptions={[disabledSecondarySpecialty]}
        onChange={(values) => setDisabledPrimarySpecialties(values)}
      />

      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Form.Item
            label="Profile Summary"
            name="profileSummary"
            rules={[
              {
                required: true,
                message: 'Please provide a summary of this candidate',
              },
              {
                max: 5000,
                message: 'Summary must be fewer than 5000 characters',
              },
            ]}
          >
            <TextArea rows={4} disabled={formSubmitting} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
